import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { workerService } from '../../../services/workerService';
import { siteService } from '../../../services/siteService';
import { companyService } from '../../../services/companyService';

const AssignSiteManagerPanel = ({ siteData, onAssigned }) => {
  const [siteManagers, setSiteManagers] = useState([]);
  const [selectedManagerId, setSelectedManagerId] = useState(siteData?.site_manager_id || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSiteManagers();
  }, []);

  useEffect(() => {
    setSelectedManagerId(siteData?.site_manager_id || '');
  }, [siteData?.site_manager_id]);

  const loadSiteManagers = async () => {
    try {
      setLoading(true);
      const companyResult = await companyService?.getUserCompany();
      let workers = [];
      if (companyResult?.data?.id) {
        const result = await workerService?.getWorkersByCompany(companyResult?.data?.id);
        workers = result?.data || [];
      } else {
        const result = await workerService?.getAllWorkers();
        workers = result?.data || [];
      }
      // Filter to site_manager role only
      setSiteManagers(workers?.filter(w => w?.role === 'site_manager'));
    } catch (err) {
      console.error('Failed to load site managers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const result = await siteService?.updateSiteManager(siteData?.id, selectedManagerId || null);
      if (result?.error) throw result.error;
      setSuccess('Site manager assigned successfully');
      onAssigned?.();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('[AssignSiteManagerPanel] assign failed:', err);
      setError('Failed to assign site manager. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const managerOptions = [
    { value: '', label: 'No Site Manager' },
    ...siteManagers?.map(m => ({ value: m?.id, label: m?.full_name || m?.email }))
  ];

  const currentManager = siteManagers?.find(m => m?.id === siteData?.site_manager_id);

  return (
    <div className="bg-muted/50 rounded-lg p-4 border border-border">
      <div className="flex items-center gap-3 mb-3">
        <div className="flex items-center justify-center w-10 h-10 bg-background rounded-lg">
          <Icon name="UserCheck" size={20} color="var(--color-accent)" />
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">Site Manager</p>
          <p className="text-xs text-muted-foreground">
            {currentManager ? currentManager?.full_name || currentManager?.email : 'Not assigned'}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Icon name="Loader2" size={14} className="animate-spin" />
          Loading managers...
        </div>
      ) : siteManagers?.length === 0 && !siteData?.site_manager_id ? (
        <p className="text-xs text-muted-foreground">
          No site managers have been invited yet. Go to <a href="/invite-users" className="text-accent underline">Invite Team Members</a> to add a site manager.
        </p>
      ) : (
        <div className="space-y-3">
          <Select
            value={selectedManagerId}
            options={managerOptions}
            onChange={setSelectedManagerId}
            placeholder="Select site manager"
          />
          {error && (
            <p className="text-xs text-destructive flex items-center gap-1">
              <Icon name="AlertCircle" size={12} />
              {error}
            </p>
          )}
          {success && (
            <p className="text-xs text-success flex items-center gap-1">
              <Icon name="CheckCircle" size={12} />
              {success}
            </p>
          )}
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving || selectedManagerId === (siteData?.site_manager_id || '')}
            className="w-full"
          >
            {saving ? (
              <><Icon name="Loader2" size={14} className="animate-spin" /> Saving...</>
            ) : (
              'Assign Manager'
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AssignSiteManagerPanel;
